<template>
  <div class="meeting">
    <div class="meeting__top">
      <meeting-indicator class="pte--auto" v-if="!app.state.matches('app.view.meeting.lobby')" />
      <a-spacer />
      <meeting-zoom class="pte--auto" />
      <meeting-device-control class="pte--auto" />
      <meeting-participants v-if="!isMobile" class="pte--auto" />
      <meeting-view-network v-if="!isMobile" class="pte--auto" />
      <meeting-exceeded-data
        v-if="notifyDataLimit100 === NotificationState.SHOWED"
        class="pte--auto"
      />
      <meeting-mobile-utility-menu v-if="isMobile" class="pte--auto" />
    </div>
    <div v-if="state.matches('participants.visible') || state.matches('participantsNetwork.visible') || state.matches('mobileUtilityMenu.visible') || state.matches('notifyDataLimit.visible')" class="meeting__overlay"></div>

    <lobby-view v-if="app.state.matches('app.view.meeting.lobby')" />
    <call-view v-if="app.state.matches('app.view.meeting.call')" />
    <draw-view v-if="app.state.matches('app.view.meeting.draw')" />
    <screensharing-view v-if="app.state.matches('app.view.meeting.screensharing')" />
    <!--<a-board v-if="app.state.matches('app.view.meeting.call') || app.state.matches('app.view.meeting.draw')" v-show="app.state.matches('app.view.meeting.draw')"/>-->
    <template v-if="!app.state.matches('app.view.meeting.lobby')">
      <meeting-board-client
        v-if="user.isClient"
        :over="app.state.matches('app.view.meeting.call') || app.state.matches('app.view.meeting.screensharing')"
      />
      <template v-else>
        <meeting-board
          :over="app.state.matches('app.view.meeting.call') || app.state.matches('app.view.meeting.screensharing')"
        />
        <meeting-overlay />
        <meeting-drop-area />
      </template>
    </template>
    <meeting-announcement />

    <Transition name="fade">
      <meeting-notification v-if="notification.hasMessage" />
    </Transition>

    <TransitionGroup name="slide-left">
      <a-toast
        v-if="state.matches('roleExchange.waitingForCounterpartyResponse')"
        type="info"
        :action="$t('toast.action.cancel')"
        loading
        overlay
        @action="roleExchangeProcedure.cancel"
        >{{ $t('roleExchange.waitingMessage') }}</a-toast
      >
      <a-toast v-if="state.matches('roleExchange.counterpartyAcceptedOffer')" type="success">{{
        $t('roleExchange.acceptedMessage')
      }}</a-toast>
      <a-toast v-if="state.matches('roleExchange.counterpartyDeniedOffer')" type="danger">{{
        $t('roleExchange.deniedMessage')
      }}</a-toast>
      <a-toast
        v-if="
          state.matches('roleExchange.offerCanceled') &&
          roleExchangeProcedure.context.counterparty.party === 'assistant'
        "
        type="info"
        >{{ $t('roleExchange.canceledMessage') }}</a-toast
      >
      <a-toast v-if="state.matches('roleExchange.partyRoleChanged')" type="info">{{
        $t('roleExchange.changedRoleMessage', {name: roleExchangeProcedure.context.role})
      }}</a-toast>
      <a-toast
        v-if="notification.hasPoorNetworkToast && !app.state.matches('app.view.meeting.lobby')"
        position="top-right"
        type="warning"
        >{{ $t('alert.titlePoorNetwork') }}</a-toast
      >

      <a-toast
        v-if="notifyDataLimit80 === NotificationState.SHOW && !app.state.matches('app.view.meeting.lobby')"
        position="top-right"
        type="warning"
        >{{ $t('alert.notifyDataLimit80') }}</a-toast
      >
      <a-toast
        v-if="notifyDataLimit100 === NotificationState.SHOW && !app.state.matches('app.view.meeting.lobby')"
        position="top-right"
        type="warning"
        hideMessage
        >{{ $t('alert.notifyDataLimit') }}</a-toast
      >
      <a-toast v-if="networkState.matches('connectionNetwork.offline')" type="warning-network">{{
        $t('notification.lostConnection', {role: roleLeave})
      }}</a-toast>
      <a-toast
        v-if="networkState.matches('connectionNetwork.online') && !roleLeave"
        type="success-network"
        class="toast-reconnect-succeed"
        >{{ $t('notification.userReconnected') }}</a-toast
      >
      <a-toast
        v-if="networkState.matches('connectionNetwork.online') && user.party !== roleLeave"
        type="success-network"
        class="toast-reconnect-succeed"
        >{{ $t('notification.participantReconnected', {role: roleLeave}) }}</a-toast
      >
    </TransitionGroup>
    <Transition name="fade">
      <a-modal
        v-if="state.matches('roleExchange.waitingForPartyResponse')"
        :title="$t('roleExchange.modalTitle')"
        @close="roleExchangeProcedure.denyOffer"
        closable
      >
        <p v-if="state.matches('roleExchange.waitingForPartyResponse')">
          {{ $t('roleExchange.offerMessage', {role: roleExchangeProcedure.context.counterparty.party}) }}
        </p>
        <template #footer>
          <a-button variant="outline-primary" @click="roleExchangeProcedure.denyOffer">{{
            $t('roleExchange.denyMessage')
          }}</a-button>
          <a-button variant="primary" @click="roleExchangeProcedure.acceptOffer">{{
            $t('roleExchange.acceptMessage')
          }}</a-button>
        </template>
      </a-modal>
    </Transition>

    <Transition name="fade">
      <a-modal
        v-if="notification.hasDialogNotification && !app.state.matches('app.view.meeting.lobby')"
        :title="$t('app.endSessionDuringRecordingConfirm')"
        @close="denyDialogNotification"
        closable
      >
        <p v-if="notification.hasDialogNotification">{{ $t('notifications.endSessionDuringRecording') }}</p>
        <template #footer>
          <a-button variant="primary" @click="endRecordingHandler">{{ $t('tools.stopRecording') }}</a-button>
          <a-button variant="danger" @click="stopCallHandler">{{ $t('tools.endSession') }}</a-button>
        </template>
      </a-modal>
    </Transition>
    <meeting-transcript />
    <meeting-tools />
    <Transition name="slide-right">
      <meeting-chat v-show="state.matches('chat.visible')" />
    </Transition>
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {useAppStore, useUserStore, useSessionStore, useNotificationStore, useExitStore} from '@/stores'
import {useMeetingMachine} from '@/state'
import {useDevicesList, useLocalStream, useWearML} from '@/composables'
import {useMediaQuery, useWakeLock} from '@vueuse/core'
import {repository} from '@communication/messages'
import useNetworkMachine from '@/state/useNetworkMachine'
import AToast from '@/components/AToast.vue'
import MeetingExceededData from '@/views/Meeting/components/MeetingExceededData.vue'
import {jsonRpc} from '@communication'
import {procedures} from '@communication'
import MeetingMobileUtilityMenu from '@/views/Meeting/components/MeetingMobileUtilityMenu.vue'

const isMobile = useMediaQuery('(max-width: 768px)')
const app = useAppStore()
const user = useUserStore()
const session = useSessionStore()
const notification = useNotificationStore()
const {state} = useMeetingMachine()
const wearML = useWearML()
const {replaceDevice} = useLocalStream()
const {request} = useWakeLock()
const roleExchangeProcedure = procedures.get('roleExchangeProcedure')
const { audioInputs: microphones } = useDevicesList()
const {state: networkState} = useNetworkMachine()
const participantDisconnectedProcedure = procedures.get('participantDisconnectedProcedure')
const exit = useExitStore()
// Enum for notification states
const NotificationState = Object.freeze({
  HIDE: 'hide',
  SHOW: 'show',
  SHOWED: 'showed',
})

// variable to notify user about 100% of data limit
const notifyDataLimit100 = ref(NotificationState.HIDE)
// variable to notify user about 80% of data limit
const notifyDataLimit80 = ref(NotificationState.HIDE)

function showDataLimitNotification(notifyRef, shouldNotify) {
  // Only show if currently hidden and condition is met
  if (notifyRef.value === NotificationState.HIDE && shouldNotify) {
    notifyRef.value = NotificationState.SHOW
    // After 2 seconds, mark as 'showed' to prevent repeat notifications
    setTimeout(() => {
      notifyRef.value = NotificationState.SHOWED
    }, 2000)
    return true
  }
  return false
}

const roleLeave = computed(() => {
  const participantRole = user.party + '_' + user.id
  if (session.participantsLeave.some(res => res === participantRole)) {
    participantDisconnectedProcedure.roleLeave = ''
    return 'You'
  } else {
    return participantDisconnectedProcedure.roleLeave
  }
})

watch(user, () => {
  if (user.dataLimit > 0) {
    const used = user.consumptionData
    // Notify at 100% data limit
    if (showDataLimitNotification(notifyDataLimit100, used >= user.dataLimit)) return
    // Notify at 80% data limit
    showDataLimitNotification(notifyDataLimit80, used >= 0.8 * user.dataLimit)
  }
})
watch(microphones, async (value, oldValue) => {
  const diffAudio = value.find((item, index) => {
    const oldItem = oldValue[index]
    return oldItem && item?.deviceId !== oldItem?.deviceId
  })

  if (diffAudio) {
    await replaceDevice('microphone', diffAudio?.deviceId, diffAudio?.label, {
      muted: {
        audio: user.hasMicrophoneMuted,
      },
    })
  }
})

onMounted(() => {
  request('screen')
  wearML.refresh()
})

function denyDialogNotification() {
  notification.showDialogNotification({
    dialogNotification: false,
  })
}

async function endRecordingHandler() {
  const isRecording = app.state.matches('app.recording.on')
  if (isRecording) {
    await procedures.get('recordingProcedure').stop()
    denyDialogNotification()
  }
}

const EXIT_LOCATION_TIMEOUT = 5000

async function stopCallHandler() {
  const isScreenSharing = app.state.matches('app.view.meeting.screensharing')
  await endRecordingHandler()
  if (isScreenSharing) {
    procedures.get('screensharingProcedure').stop()
  }
  if (user.isAssistant) {
    // The location only feeds the exit summary. destroy() (con:disjoin, ends the session for the others)
    // runs exactly once: on the reply, whatever its shape, or after EXIT_LOCATION_TIMEOUT without one
    let ended = false
    let locationTimer = null
    const end = () => {
      if (ended) return
      ended = true
      clearTimeout(locationTimer)
      procedures.get('destroyProcedure').destroy()
    }
    locationTimer = setTimeout(end, EXIT_LOCATION_TIMEOUT)
    jsonRpc.send(repository.fetchEventsMessage('location'), (error, response) => {
      try {
        if (!error && !ended) {
          const raw = typeof response?.value === 'string' ? response.value : response
          const events = typeof raw === 'string' ? JSON.parse(raw) : raw
          const filtered = Array.isArray(events) ? events.filter(item => item?.party === 'client') : []
          if (filtered.length) {
            const {latitude, longitude, source} = filtered[0].location ?? {}
            const coords = [latitude, longitude]
            if (coords.every(value => typeof value === 'number')) {
              exit.setLocation(coords, source)
            }
          }
        }
      } catch (locationError) {
        console.warn('Client location for the exit summary is not available', locationError)
      }
      end()
    })
  } else {
    procedures.get('destroyProcedure').destroy()
  }
}
</script>

<style lang="scss">
.meeting {
  .toast-reconnect-succeed {
    @media (max-width: 500px) {
      font-size: 13px;
    }
  }

  &__top {
    display: flex;
    position: fixed;
    width: 100%;
    padding: $box-spacer;
    z-index: ($zindex-overlay + 2);
    pointer-events: none;
    flex-wrap: wrap;
    gap: 5px;
    justify-content: flex-end;
  }
  &__overlay {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: $zindex-overlay;
  }
}
</style>
